import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Mail, Plus, Save, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { SeaServiceEditor } from "@/components/SeaServiceEditor";
import { usePipelineMeta } from "@/lib/pipeline";

const FIELDS = [["full_name", "Full name"], ["rank", "Rank applied for"], ["dob", "Date of birth", "date"], ["nationality", "Nationality"],
  ["passport_number", "Passport no."], ["passport_expiry", "Passport expiry", "date"], ["cdc_number", "CDC no."], ["cdc_expiry", "CDC expiry", "date"],
  ["vessel_name", "Proposed vessel"], ["principal_name", "Principal"], ["proposal_date", "Date of proposal", "date"]];
const BLANK_CERT = { name: "", number: "", issue_date: "", expiry_date: "" };
const subjectOf = (d) => `Crew Proposal — ${d.full_name || "—"} — ${d.rank || "—"} — ${d.vessel_name || "—"}`;

function Certificates({ rows, onChange }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2">
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm min-w-[640px]" data-testid="draft-cert-table">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500"><tr>{["Certificate", "Number", "Issued", "Valid until", ""].map((h) => <th key={h} className="px-2 py-2 text-left font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-400">No certificates.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100" data-testid={`draft-cert-row-${i}`}>
                {[["name", "text"], ["number", "text"], ["issue_date", "date"], ["expiry_date", "date"]].map(([k, t]) => (
                  <td key={k} className="px-1.5 py-1.5"><Input type={t} value={r[k] || ""} aria-label={k} className="h-8 text-sm" onChange={(e) => set(i, k, e.target.value)} data-testid={`draft-cert-${k}-${i}`} /></td>
                ))}
                <td className="px-1"><Button type="button" size="icon" variant="ghost" onClick={() => onChange(rows.filter((_, j) => j !== i))} aria-label="Remove certificate" data-testid={`draft-cert-remove-${i}`}><Trash2 className="w-4 h-4" /></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="outline" size="sm" onClick={() => onChange([...rows, { ...BLANK_CERT }])} data-testid="draft-cert-add-btn"><Plus className="w-4 h-4 mr-1" /> Add certificate</Button>
    </div>
  );
}

function EmailDialog({ open, onOpenChange, p, dirty, onSent }) {
  const [pid, setPid] = useState("");
  const [sending, setSending] = useState(false);
  const withEmail = p.principals.filter((x) => x.email);
  useEffect(() => { if (open) setPid(withEmail.find((x) => x.name === p.data.principal_name)?.id || ""); }, [open]); // eslint-disable-line
  const send = async () => {
    setSending(true);
    try { const { data } = await api.post(`/proposals/${p.id}/send`, { principal_id: pid }); toast.success(`Proposal emailed to ${data.to}`); onOpenChange(false); onSent(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="proposal-email-dialog">
        <DialogHeader><DialogTitle>Email proposal to principal</DialogTitle><DialogDescription>The email contains the formatted proposal and a secure PDF download link (valid 30 days).</DialogDescription></DialogHeader>
        {dirty && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="proposal-email-unsaved">Save your changes first — the last saved version is what gets sent.</div>}
        <div className="space-y-4">
          <div className="space-y-1.5"><Label>To</Label>
            {withEmail.length ? (
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger data-testid="proposal-email-to-select"><SelectValue placeholder="Select principal email" /></SelectTrigger>
                <SelectContent>{withEmail.map((x) => <SelectItem key={x.id} value={x.id}>{x.name} · {x.email}</SelectItem>)}</SelectContent>
              </Select>
            ) : <div className="text-sm text-slate-500" data-testid="proposal-email-no-principals">No principal emails saved yet. Ask Super Admin to add them in Master Data → Principals.</div>}
          </div>
          <div className="space-y-1.5"><Label>Subject</Label><div className="text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2" data-testid="proposal-email-subject">{subjectOf(p.data)}</div></div>
        </div>
        <DialogFooter><Button onClick={send} disabled={!pid || dirty || sending} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="proposal-email-send-btn">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-1" /> Send email</>}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ProposalDraftPage() {
  const { id } = useParams();
  const meta = usePipelineMeta();
  const [p, setP] = useState(null);
  const [d, setD] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const load = () => api.get(`/proposals/${id}`).then(({ data }) => { setP(data); setD({ ...data.data }); setDirty(false); }).catch((err) => { toast.error(formatApiError(err)); setP(false); });
  useEffect(() => { load(); }, [id]); // eslint-disable-line
  if (p === null || !meta) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400 inline" /></div>;
  if (p === false) return <div className="py-20 text-center text-slate-500" data-testid="proposal-not-found">Proposal not found.</div>;
  const upd = (patch) => { setD({ ...d, ...patch }); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { await api.put(`/proposals/${id}`, d); toast.success("Proposal draft saved"); await load(); }
    catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };
  const pdf = async () => {
    try {
      const { data } = await api.get(`/proposals/${id}/pdf`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = `Crew-Proposal-${(p.data.full_name || "candidate").replace(/\s+/g, "-")}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 60000);
    } catch { toast.error("Could not download the PDF, please try again"); }
  };
  return (
    <div className="space-y-6" data-testid="proposal-draft-page">
      <Link to={`/candidates/${p.candidate_id}`} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800"><ArrowLeft className="w-4 h-4" /> Candidate profile</Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">Proposal draft · {p.status === "sent" ? "Sent" : "Not sent yet"}</div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }} data-testid="proposal-title">{subjectOf(d)}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={pdf} disabled={dirty} title={dirty ? "Save first" : undefined} data-testid="proposal-download-pdf-btn"><Download className="w-4 h-4 mr-1" /> Download PDF</Button>
          {p.can_edit && <Button variant="outline" onClick={() => setEmailOpen(true)} data-testid="proposal-email-btn"><Mail className="w-4 h-4 mr-1" /> Email to principal</Button>}
          {p.can_edit && <Button onClick={save} disabled={saving || !dirty} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid="proposal-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> Save changes</>}</Button>}
        </div>
      </div>
      <fieldset disabled={!p.can_edit} className="space-y-6 min-w-0">
        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-sm font-semibold text-slate-800 mb-3">Candidate & proposal</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {FIELDS.map(([k, l, t]) => (
              <div key={k} className="space-y-1.5"><Label htmlFor={`draft-${k}`}>{l}</Label>
                <Input id={`draft-${k}`} type={t || "text"} value={d[k] || ""} onChange={(e) => upd({ [k]: e.target.value })} data-testid={`draft-${k}-input`} /></div>
            ))}
          </div>
        </section>
        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-sm font-semibold text-slate-800 mb-3">Certificates</div>
          <Certificates rows={d.certificates || []} onChange={(rows) => upd({ certificates: rows })} />
        </section>
        <section className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="text-sm font-semibold text-slate-800 mb-1">Sea service</div>
          <div className="text-xs text-slate-500 mb-3">Durations, per-rank totals and overall total recalculate from the From / To dates.</div>
          <SeaServiceEditor rows={d.sea_service || []} onChange={(rows) => upd({ sea_service: rows })} ranks={meta.ranks} vesselTypes={meta.vessel_types} prefix="draft-sea" />
        </section>
      </fieldset>
      {p.sends.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-xl p-5" data-testid="proposal-send-history">
          <div className="text-sm font-semibold text-slate-800 mb-2">Email history</div>
          {p.sends.map((s, i) => <div key={i} className="text-sm text-slate-600">{new Date(s.at).toLocaleString()} · to {s.principal} ({s.to}) · by {s.by_name}</div>)}
        </section>
      )}
      <EmailDialog open={emailOpen} onOpenChange={setEmailOpen} p={p} dirty={dirty} onSent={load} />
    </div>
  );
}
